import AsyncStorage from '@react-native-async-storage/async-storage';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, focusManager, onlineManager } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import * as Network from 'expo-network';
import React, { useEffect, useState } from 'react';
import type { AppStateStatus } from 'react-native';
import { AppState, Platform } from 'react-native';

// Query Client Configuration
const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24,  // 24 hour cache time for inactive queries (garbage collection)
        staleTime: 1000 * 60 * 5,     // 5 minute stale time for active queries
        refetchOnWindowFocus: true,   // Enable refetching on window focus
        refetchOnReconnect: true,     // Enable refetching on reconnect
        networkMode: 'online',        // Network mode for better offline handling

        // Retry failed requests 3 times with exponential backoff
        retry: (failureCount, error) => {
          if (error && 'status' in error) {
            const status = (error as any).status;
            // Don't retry for client errors (4xx)
            if (status >= 400 && status < 500) {
              return false;
            }
          }
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
      mutations: {
        // Retry mutations once on network error
        retry: (failureCount, error) => {
          if (error && 'name' in error && error.name === 'NetworkError') {
            return failureCount < 1;
          }
          return false;
        },
        networkMode: 'online',
      },
    },
  });
};

// AsyncStorage Persister Configuration
const createPersister = () => {
  return createAsyncStoragePersister({
    storage: AsyncStorage,
    key: 'flavrly-query-cache',
    serialize: JSON.stringify,
    deserialize: JSON.parse,
    // Only persist successful queries, not errors
    // Note: filters option removed in newer versions, using dehydrateOptions instead
  });
};

// React Native Focus Management
const useAppStateFocus = () => {
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (status: AppStateStatus) => {
      if (Platform.OS !== 'web') {
        focusManager.setFocused(status === 'active');
      }
    });

    return () => subscription?.remove();
  }, []);
};

// React Native Network Status Management
const useNetworkStatus = () => {
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval>;
    let startupDelayId: ReturnType<typeof setTimeout>;

    // Assume online initially to avoid blocking startup
    onlineManager.setOnline(true);

    const checkNetworkStatus = async () => {
      try {
        const networkState = await Network.getNetworkStateAsync();
        const isOnline = !!networkState.isConnected && !!networkState.isInternetReachable;
        onlineManager.setOnline(isOnline);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn('expo-network not available, assuming online connection:', errorMessage);
        onlineManager.setOnline(true);
      }
    };

    // Defer first check and polling to let critical renders complete
    startupDelayId = setTimeout(() => {
      checkNetworkStatus();
      intervalId = setInterval(checkNetworkStatus, 10000);
    }, 5000);

    return () => {
      clearTimeout(startupDelayId);
      clearInterval(intervalId);
    };
  }, []);
};

// Query Provider Component
interface QueryProviderProps {
  children: React.ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => createQueryClient());
  const [persister] = useState(() => createPersister());

  // Set up React Native specific integrations
  useAppStateFocus();
  useNetworkStatus();

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        buster: 'flavrly-v1', // Change this to invalidate cache on app updates
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist queries that explicitly opt in via meta.persist === true
            // This ensures sensitive data (profile, notifications) is not persisted by default
            const shouldPersist = query.meta?.persist === true;

            if (!shouldPersist) {
              return false;
            }

            // Additional safety checks: only persist successful queries that aren't too old
            const queryIsOld = query.state.dataUpdatedAt < Date.now() - 1000 * 60 * 60 * 12; // 12 hours
            return query.state.status === 'success' && !queryIsOld;
          },
        },
      }}
      onSuccess={() => {
        console.log('Query cache restored successfully');
      }}
      onError={() => {
        console.error('Error restoring query cache');
      }}
    >
      {children}
    </PersistQueryClientProvider>
  );
}

export default QueryProvider;
